import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    CircularProgress,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';

const StudentDebtDashboard = () => {
    const theme = useTheme();
    const textColor = theme.palette.text.primary;
    const green = theme.palette.success.main;  // Paid
    const blue = theme.palette.info.main;      // Reduced

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [site, setSite] = useState('All Sites');
    const [selectedRows, setSelectedRows] = useState([]);
    const [template, setTemplate] = useState('Warning');

    // map UI labels to the actual HTML templates in /public/emails
    const templateFileMap = {
        Warning: 'negbalreminder.html',   // 1st / warning notice
        '2nd Notice': 'secondnotice.html',
        '3rd Notice': 'thirdnotice.html',
        'Final Notice': 'finalnotice.html',
    };

    // new state for email progress
    const [sending, setSending] = useState(false);
    const [emailProgress, setEmailProgress] = useState([]); // each entry: { id, label, status }

    const formatCurrency = (val) =>
        Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await fetch('https://figazuresql.swancreate.com/api/swa/Students');
                if (!res.ok) throw new Error(`Students HTTP ${res.status}`);
                setStudents(await res.json());
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const sites = useMemo(() => {
        const names = Array.from(
            new Set(students.map(r => r.SchoolName).filter(Boolean))
        ).sort();
        return ['All Sites', ...names];
    }, [students]);

    const allNegative = useMemo(
        () => students.filter(s => Number(s.Account_Balance) < 0),
        [students]
    );

    const negativeFiltered = useMemo(
        () =>
            site === 'All Sites'
                ? allNegative
                : allNegative.filter(s => s.SchoolName === site),
        [allNegative, site]
    );

    const negativeByStatus = useMemo(() => {
        const stats = { total: 0, P: { count: 0, sum: 0 }, R: { count: 0, sum: 0 } };
        negativeFiltered.forEach(s => {
            const bal = Number(s.Account_Balance);
            stats.total += bal;
            if (s.PaymentStatus === 'P') {
                stats.P.count++;
                stats.P.sum += bal;
            }
            if (s.PaymentStatus === 'R') {
                stats.R.count++;
                stats.R.sum += bal;
            }
        });
        return stats;
    }, [negativeFiltered]);

    const pieData = [
        { id: 'P', label: 'Meal Status: Paid', value: negativeByStatus.P.count, color: green },
        { id: 'R', label: 'Meal Status: Reduced', value: negativeByStatus.R.count, color: blue },
    ];

    const barData = useMemo(() => {
        const group = {};
        allNegative.forEach(s => {
            const key = s.SchoolName || 'Unknown';
            group[key] = (group[key] || 0) + Number(s.Account_Balance);
        });
        return Object.entries(group).map(([siteName, debt]) => ({ site: siteName, debt }));
    }, [allNegative]);

    const topSites = useMemo(
        () => [...barData].sort((a, b) => a.debt - b.debt).slice(0, 10),
        [barData]
    );

    const tableRows = useMemo(() => {
        return negativeFiltered
            .filter(r => r.PaymentStatus === 'P' || r.PaymentStatus === 'R')
            .map((r, i) => ({ id: i, ...r }));
    }, [negativeFiltered]);

    const columns = [
        { field: 'FirstName', headerName: 'Student First Name', flex: 1 },
        { field: 'LastName', headerName: 'Student Last Name', flex: 1 },
        { field: 'guardian_FName', headerName: 'Guardian First Name', flex: 1 },
        { field: 'guardian_LName', headerName: 'Guardian Last Name', flex: 1 },
        { field: 'guardian_Email', headerName: 'Guardian Email', flex: 1.5 },
        { field: 'Grade', headerName: 'Grade', flex: 0.5 },
        {
            field: 'Account_Balance',
            headerName: 'Balance',
            flex: 1,
            valueFormatter: ({ value }) => formatCurrency(value),
        },
        { field: 'PaymentStatus', headerName: 'Meal Status', flex: 0.7 },
    ];

    const downloadCSV = () => {
        const header = [
            'Student First',
            'Student Last',
            'Guardian First',
            'Guardian Last',
            'Guardian Email',
            'Grade',
            'Balance',
            'Meal Status'
        ];
        const rows = tableRows.map(r => [
            r.FirstName,
            r.LastName,
            r.guardian_FName,
            r.guardian_LName,
            r.guardian_Email,
            r.Grade,
            r.Account_Balance,
            r.PaymentStatus
        ]);
        const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'students_negative.csv';
        link.click();
    };

    const sendEmailNotifications = async () => {
        setSending(true);
        setEmailProgress([]);

        // choose the correct HTML template file
        const templateFile = templateFileMap[template] || 'negbalreminder.html';
        const templateHtml = await fetch(`/emails/${templateFile}`).then(r => r.text());

        for (const id of selectedRows) {
            const student = tableRows.find(r => r.id === id);
            const label = student
                ? `${student.guardian_FName} ${student.guardian_LName}`
                : `Row ${id}`;

            if (!student || !student.guardian_Email) {
                setEmailProgress(prev => [
                    ...prev,
                    { id, label, status: 'Skipped' }
                ]);
                continue;
            }

            setEmailProgress(prev => [
                ...prev,
                { id, label, status: 'Sending…' }
            ]);

            const payLink =
                'https://nbfinance.figk-12.com/paymentcollectionpage' +
                `?student=${encodeURIComponent(`${student.FirstName} ${student.LastName}`)}` +
                `&site=${encodeURIComponent(student.SchoolName)}` +
                `&balance=${encodeURIComponent(student.Account_Balance)}` +
                `&apiid=${encodeURIComponent(student.apiid)}`;

            const html = templateHtml
                .replace(/{{guardian_FName}}/g, student.guardian_FName)
                .replace(/{{guardian_LName}}/g, student.guardian_LName)
                .replace(/{{FirstName}}/g, student.FirstName)
                .replace(/{{LastName}}/g, student.LastName)
                .replace(/{{SchoolName}}/g, student.SchoolName)
                .replace(/{{Account_Balance}}/g, formatCurrency(student.Account_Balance))
                .replace(/{{PaymentLink}}/g, payLink)
                .replace(/{{CurrentYear}}/g, new Date().getFullYear());

            const text = `
Hello ${student.guardian_FName} ${student.guardian_LName},

Your student's meal account at ${student.SchoolName} has a negative balance of ${formatCurrency(
                student.Account_Balance
            )}.
Please add funds here: ${payLink}
`.trim();

            try {
                const resp = await fetch('https://mailgun.swancreate.com/send', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        from: 'postmaster@figk-12.com',
                        to: student.guardian_Email,
                        subject: `${template} Notice: Negative Lunch Account Balance`,
                        html,
                        text,
                    }),
                });
                if (!resp.ok) throw new Error(`Mailgun ${resp.status}`);
                setEmailProgress(prev =>
                    prev.map(e => e.id === id ? { ...e, status: 'Sent ✅' } : e)
                );
            } catch (e) {
                console.error(e);
                setEmailProgress(prev =>
                    prev.map(e => e.id === id ? { ...e, status: 'Failed ❌' } : e)
                );
            }
        }

        setSending(false);
        alert(`"${template}" emails processed.`);
    };

    return (
        <Box m="20px">
            <Typography variant="h4" mb="20px" color={textColor}>
                Student Debt Management
            </Typography>

            <FormControl sx={{ mb: 3, minWidth: 240 }}>
                <InputLabel sx={{ color: textColor }}>Site</InputLabel>
                <Select
                    value={site}
                    onChange={e => setSite(e.target.value)}
                    sx={{
                        color: textColor,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: textColor }
                    }}
                >
                    {sites.map(s => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit,minmax(160px,1fr))"
                gap="20px"
                mb="40px"
            >
                {/* Summary cards */}
                <Box
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        boxShadow: `0 0 12px ${theme.palette.primary.main}66`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Typography variant="h5" color={textColor}>
                        {formatCurrency(negativeByStatus.total)}
                    </Typography>
                    <Typography variant="subtitle2" color={theme.palette.primary.main}>
                        Total Negative
                    </Typography>
                </Box>
                <Box
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        border: `2px solid ${green}`,
                        borderRadius: 2,
                        boxShadow: `0 0 12px ${green}66`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Typography variant="h5" color={textColor}>
                        {formatCurrency(negativeByStatus.P.sum)}
                    </Typography>
                    <Typography variant="subtitle2" color={green}>
                        Meal Status: Paid
                    </Typography>
                </Box>
                <Box
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        border: `2px solid ${blue}`,
                        borderRadius: 2,
                        boxShadow: `0 0 12px ${blue}66`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Typography variant="h5" color={textColor}>
                        {formatCurrency(negativeByStatus.R.sum)}
                    </Typography>
                    <Typography variant="subtitle2" color={blue}>
                        Meal Status: Reduced
                    </Typography>
                </Box>
            </Box>

            {/* Charts and grids */}
            <Box display="flex" gap={2} mb="40px">
                <Box flex={1} height={300}>
                    <ResponsivePie
                        data={pieData}
                        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                        colors={d => d.data.color}
                        theme={{
                            textColor,
                            tooltip: {
                                container: {
                                    background: theme.palette.background.paper,
                                    color: textColor,
                                },
                            },
                        }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsTextColor={textColor}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    />
                </Box>
                <Box flex={1} component={Paper} p={2}>
                    <Typography variant="h6" gutterBottom color={textColor}>
                        Top 10 Sites by Negative Debt
                    </Typography>
                    <DataGrid
                        rows={topSites.map((d, i) => ({
                            id: i,
                            rank: i + 1,
                            site: d.site,
                            debt: d.debt,
                        }))}
                        columns={[
                            { field: 'rank', headerName: '#', width: 50 },
                            { field: 'site', headerName: 'Site', flex: 1 },
                            {
                                field: 'debt', headerName: 'Debt', width: 120,
                                valueFormatter: ({ value }) => formatCurrency(value),
                            },
                        ]}
                        autoHeight
                        hideFooter
                        disableSelectionOnClick
                        rowHeight={28}
                        headerHeight={30}
                        sx={{
                            '& .MuiDataGrid-row:nth-of-type(odd)': {
                                backgroundColor: theme.palette.action.hover,
                            },
                            '& .MuiDataGrid-row:nth-of-type(even)': {
                                backgroundColor: theme.palette.background.default,
                            },
                            '.MuiDataGrid-cell': { py: 0.5, color: textColor },
                            '.MuiDataGrid-columnHeaders': {
                                backgroundColor: theme.palette.background.paper,
                                color: textColor,
                            },
                        }}
                    />
                </Box>
            </Box>

            <Box component={Paper} p={2} mb={4} height={400}>
                <Typography mb={2} color={textColor}>
                    Total Negative Debt by Site
                </Typography>
                <ResponsiveBar
                    data={barData}
                    keys={['debt']}
                    indexBy="site"
                    margin={{ top: 20, right: 20, bottom: 150, left: 60 }}
                    padding={0.3}
                    colors={[blue]}
                    theme={{
                        axis: {
                            ticks: { text: { fill: textColor } },
                            legend: { text: { fill: textColor } },
                        },
                        tooltip: {
                            container: {
                                background: theme.palette.background.paper,
                                color: textColor,
                            },
                        },
                    }}
                    axisBottom={{ tickRotation: -45 }}
                    enableLabel={false}
                    tooltip={({ indexValue, value }) => (
                        <Box
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.background.paper,
                                color: textColor,
                                borderRadius: 1,
                            }}
                        >
                            {`${indexValue}: ${formatCurrency(value)}`}
                        </Box>
                    )}
                    onClick={({ data }) => setSite(data.site)}
                />
            </Box>

            <Box display="flex" gap={2} mb={2} alignItems="center">
                <Button
                    variant="contained"
                    sx={{ backgroundColor: green, '&:hover': { backgroundColor: green } }}
                    onClick={downloadCSV}
                >
                    Download CSV
                </Button>

                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>Choose Template</InputLabel>
                    <Select
                        value={template}
                        label="Choose Template"
                        onChange={e => setTemplate(e.target.value)}
                    >
                        <MenuItem value="Warning">Warning</MenuItem>
                        <MenuItem value="2nd Notice">2nd Notice</MenuItem>
                        <MenuItem value="3rd Notice">3rd Notice</MenuItem>
                        <MenuItem value="Final Notice">Final Notice</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    disabled={selectedRows.length === 0 || sending}
                    onClick={sendEmailNotifications}
                >
                    {sending ? 'Sending…' : `Send "${template}" Emails`}
                </Button>
            </Box>

            {/* live email progress */}
            {emailProgress.length > 0 && (
                <Paper sx={{ p: 2, mb: 3, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Email Send Progress:
                    </Typography>
                    <List dense>
                        {emailProgress.map(({ id, label, status }) => (
                            <ListItem key={id}>
                                <ListItemText
                                    primary={label}
                                    secondary={status}
                                    secondaryTypographyProps={{
                                        color:
                                            status.startsWith('Sent') ? 'success.main' :
                                                status.startsWith('Failed') ? 'error.main' :
                                                    'text.secondary'
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Box height={500}>
                <DataGrid
                    rows={tableRows}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    checkboxSelection
                    selectionModel={selectedRows}
                    onSelectionModelChange={newSel => setSelectedRows(newSel)}
                    sx={{
                        '.MuiDataGrid-columnHeaders': {
                            backgroundColor: theme.palette.background.paper,
                            color: textColor,
                        },
                        '.MuiDataGrid-cell': { color: textColor },
                        '.MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: theme.palette.background.default,
                        },
                        '.MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default StudentDebtDashboard;
